// DrawingUtils.ts - Utility functions for handling drawing on OpenLayers maps

import { Draw, Modify, Snap } from 'ol/interaction';
import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Style, Fill, Stroke, Circle } from 'ol/style';
import { GeoJSON } from 'ol/format';
import { Map } from 'ol';
import { getArea } from 'ol/sphere';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';

export class DrawingManager {
  private map: Map;
  private drawSource: VectorSource;
  private drawLayer: VectorLayer<VectorSource>;
  private drawInteraction: Draw | null = null;
  private modifyInteraction: Modify | null = null;
  private snapInteraction: Snap | null = null;
  private onFeatureDrawn?: (geoJsonFeature: any) => void;

  constructor(map: Map, onFeatureDrawn?: (geoJsonFeature: any) => void) {
    this.map = map;
    this.onFeatureDrawn = onFeatureDrawn;
    this.initializeDrawingLayer();
  }

  private initializeDrawingLayer() {
    // Create drawing source and layer
    this.drawSource = new VectorSource();

    this.drawLayer = new VectorLayer({
      source: this.drawSource,
      style: new Style({
        fill: new Fill({
          color: 'rgba(59, 130, 246, 0.3)',
        }),
        stroke: new Stroke({
          color: '#3B82F6',
          width: 3,
        }),
        image: new Circle({
          radius: 6,
          fill: new Fill({
            color: '#3B82F6',
          }),
          stroke: new Stroke({
            color: '#FFFFFF',
            width: 2,
          }),
        }),
      }),
    });

    this.map.addLayer(this.drawLayer);
    this.initializeInteractions();
  }

  private initializeInteractions() {
    // Create draw interaction for polygons
    this.drawInteraction = new Draw({
      source: this.drawSource,
      type: 'Polygon',
      style: new Style({
        fill: new Fill({
          color: 'rgba(59, 130, 246, 0.2)',
        }),
        stroke: new Stroke({
          color: '#3B82F6',
          width: 3,
          lineDash: [10, 10],
        }),
        image: new Circle({
          radius: 6,
          fill: new Fill({
            color: '#3B82F6',
          }),
          stroke: new Stroke({
            color: '#FFFFFF',
            width: 2,
          }),
        }),
      }),
    });

    // Create modify interaction
    this.modifyInteraction = new Modify({ 
      source: this.drawSource,
      style: new Style({
        image: new Circle({
          radius: 8,
          fill: new Fill({
            color: '#FF6B6B',
          }),
          stroke: new Stroke({
            color: '#FFFFFF',
            width: 2,
          }),
        }),
      }),
    });

    // Create snap interaction
    this.snapInteraction = new Snap({ source: this.drawSource });

    // Handle draw end event
    this.drawInteraction.on('drawend', (event) => {
      const feature = event.feature;
      const geometry = feature.getGeometry();
      
      if (geometry && this.onFeatureDrawn) {
        // Convert to GeoJSON
        const geoJsonFormat = new GeoJSON();
        const geoJsonFeature = geoJsonFormat.writeFeatureObject(feature, {
          featureProjection: this.map.getView().getProjection(),
          dataProjection: 'EPSG:4326',
        });

        console.log('Feature drawn:', geoJsonFeature);
        this.onFeatureDrawn(geoJsonFeature);
      }
    });
  }

  enableDrawing() {
    if (this.drawInteraction) {
      this.map.addInteraction(this.drawInteraction);
    }
    if (this.modifyInteraction) {
      this.map.addInteraction(this.modifyInteraction);
    }
    if (this.snapInteraction) {
      this.map.addInteraction(this.snapInteraction);
    }
    
    // Change cursor
    this.map.getTargetElement().style.cursor = 'crosshair';
  }

  disableDrawing() {
    if (this.drawInteraction) {
      this.map.removeInteraction(this.drawInteraction);
    }
    if (this.modifyInteraction) {
      this.map.removeInteraction(this.modifyInteraction);
    }
    if (this.snapInteraction) {
      this.map.removeInteraction(this.snapInteraction);
    }
    
    // Reset cursor
    this.map.getTargetElement().style.cursor = '';
  }

  clearDrawing() {
    this.drawSource.clear();
  }

  cleanup() {
    this.disableDrawing();
    if (this.drawLayer) {
      this.map.removeLayer(this.drawLayer);
    }
  }

  // Get drawn features as GeoJSON
  getDrawnFeatures(): any[] {
    const geoJsonFormat = new GeoJSON();
    const features = this.drawSource.getFeatures();
    
    return features.map(feature => 
      geoJsonFormat.writeFeatureObject(feature, {
        featureProjection: this.map.getView().getProjection(),
        dataProjection: 'EPSG:4326',
      })
    );
  }

  // Load existing GeoJSON feature for editing
  loadGeoJsonFeature(geoJsonFeature: any) {
    const geoJsonFormat = new GeoJSON();
    const feature = geoJsonFormat.readFeature(geoJsonFeature, {
      featureProjection: this.map.getView().getProjection(),
      dataProjection: 'EPSG:4326',
    });
    
    this.drawSource.addFeature(feature);
  }
}

/** GeoJSON Feature from ``drawnField.geometry`` (Feature or Polygon) or a raw Feature / Polygon. */
export function normalizeDrawnFieldToFeature(drawnField: any): any {
  if (!drawnField) return null;
  const raw = drawnField.geometry;
  if (!raw) return null;
  if (raw.type === 'Feature') return raw;
  if (raw.type === 'Polygon' || raw.type === 'MultiPolygon') return { type: 'Feature', properties: {}, geometry: raw };
  return null;
}

function toPolygonFeature(geoJsonInput: any): any {
  if (!geoJsonInput) return null;
  if (
    geoJsonInput.type === 'Feature' &&
    (geoJsonInput.geometry?.type === 'Polygon' || geoJsonInput.geometry?.type === 'MultiPolygon')
  ) {
    return geoJsonInput;
  }
  if (geoJsonInput.type === 'Polygon' || geoJsonInput.type === 'MultiPolygon') {
    return { type: 'Feature', properties: {}, geometry: geoJsonInput };
  }
  if (
    geoJsonInput.geometry?.type === 'Feature' ||
    geoJsonInput.geometry?.type === 'Polygon' ||
    geoJsonInput.geometry?.type === 'MultiPolygon'
  ) {
    return normalizeDrawnFieldToFeature(geoJsonInput);
  }
  return null;
}

/** Geodesic area (EPSG:4326) in hectares using OpenLayers sphere utilities. */
export function calculatePolygonAreaHectares(geoJsonInput: any): number {
  const feature = toPolygonFeature(geoJsonInput);
  if (
    !feature?.geometry ||
    (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon')
  ) {
    return 0;
  }

  const geoJsonFormat = new GeoJSON();
  const olFeature = geoJsonFormat.readFeature(feature, {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:4326',
  });
  const g = olFeature.getGeometry();
  if (!g) return 0;
  // GeoJSON is WGS84; ol/sphere getArea defaults to EPSG:3857 and would mis-transform 4326 coords.
  const m2 = getArea(g, { projection: 'EPSG:4326' });
  return Math.round((m2 / 10000) * 100) / 100;
}

/**
 * Flatten GeoJSON Polygon / MultiPolygon into digisaka.app ``polygons``:
 * an array of exterior rings ``[[lng,lat], ...]``.
 */
export function geoJsonGeometryToPolygonRings(
  geometry: { type?: string; coordinates?: unknown } | null | undefined
): number[][][] {
  if (!geometry?.type || !Array.isArray(geometry.coordinates)) return [];
  const coords = geometry.coordinates as unknown[];

  if (geometry.type === 'Polygon') {
    // coordinates: [exterior, ...holes] — digisaka farm blocks use exterior rings
    const exterior = coords[0];
    if (
      Array.isArray(exterior) &&
      exterior.length > 0 &&
      Array.isArray(exterior[0]) &&
      typeof (exterior[0] as number[])[0] === 'number'
    ) {
      return [exterior as number[][]];
    }
    return [];
  }

  if (geometry.type === 'MultiPolygon') {
    const rings: number[][][] = [];
    for (const poly of coords) {
      if (!Array.isArray(poly) || !poly.length) continue;
      const exterior = poly[0];
      if (
        Array.isArray(exterior) &&
        exterior.length > 0 &&
        Array.isArray(exterior[0]) &&
        typeof (exterior[0] as number[])[0] === 'number'
      ) {
        rings.push(exterior as number[][]);
      }
    }
    return rings;
  }

  return [];
}

/**
 * A point inside the polygon (for point-in-polygon / admin lookups).
 * Prefer over vertex-averaged centroid for concave shapes.
 */
export function getPolygonInteriorLonLat(geoJsonInput: any): [number, number] | null {
  const feature = toPolygonFeature(geoJsonInput);
  if (
    !feature?.geometry ||
    (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon')
  ) {
    return null;
  }

  const geoJsonFormat = new GeoJSON();
  const olFeature = geoJsonFormat.readFeature(feature, {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:4326',
  });
  const g = olFeature.getGeometry();
  if (!g) return null;
  let poly: Polygon | null = null;
  if (g instanceof Polygon) {
    poly = g;
  } else if (g instanceof MultiPolygon) {
    poly = g.getPolygon(0) ?? null;
  }
  if (!poly) return null;
  const ip = poly.getInteriorPoint();
  const c = ip.getCoordinates();
  return [c[0], c[1]];
}

/** Fallback: average of outer ring vertices (EPSG:4326). */
export function getPolygonCentroid(geoJsonFeature: any): [number, number] | null {
  const feature = toPolygonFeature(geoJsonFeature);
  if (!feature?.geometry) return null;
  const coords =
    feature.geometry.type === 'Polygon'
      ? feature.geometry.coordinates[0]
      : feature.geometry.type === 'MultiPolygon'
        ? feature.geometry.coordinates[0]?.[0]
        : null;
  if (!coords?.length) return null;

  const coordinates = coords;
  let x = 0;
  let y = 0;

  for (const [lng, lat] of coordinates) {
    x += lng;
    y += lat;
  }

  return [x / coordinates.length, y / coordinates.length];
}

// Helper function to validate GeoJSON polygon
export function isValidPolygon(geoJsonFeature: any): boolean {
  const feature = toPolygonFeature(geoJsonFeature);
  if (!feature?.geometry || feature.geometry.type !== 'Polygon') {
    return false;
  }

  const coordinates = feature.geometry.coordinates;
  
  // Check if it has at least one ring
  if (!coordinates || coordinates.length === 0) {
    return false;
  }

  // Check if the first ring has at least 4 points (3 unique + closing point)
  const outerRing = coordinates[0];
  if (!outerRing || outerRing.length < 4) {
    return false;
  }

  // Check if the first and last points are the same (closed ring)
  const firstPoint = outerRing[0];
  const lastPoint = outerRing[outerRing.length - 1];
  
  return (
    firstPoint[0] === lastPoint[0] && 
    firstPoint[1] === lastPoint[1]
  );
}